import {
  Box,
  Button,
  Typography,
  TextField,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import { generateEmailCode, VerifyEmailCode, generateSmsCode, VerifySmsCode } from "../services/api";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const HotelAdd_Information_4 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { hotelData, saveStep, fetchDraft, loading } = useHotel();
  const [formData, setFormData] = useState({
    phoneNumber: "",
    secondPhoneNumber: "",
    email: "",
    whatsapp: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [emailVerificationStep, setEmailVerificationStep] = useState("input"); // 'input', 'code', 'verified'
  const [verificationCode, setVerificationCode] = useState("");
  const [isEmailCodeSending, setIsEmailCodeSending] = useState(false);
  const [isEmailVerifying, setisEmailVerifying] = useState(false);
  const [emailForVerification, setEmailForVerification] = useState("");
  const [saveError, setSaveError] = useState("");

  const [phoneVerificationStep, setPhoneVerificationStep] = useState("input"); // 'input', 'code', 'verified'
  const [phoneSmsCode, setPhoneSmsCode] = useState("");
  const [isCodeSending, setIsCodeSending] = useState(false);
  const [isCodeVerifying, setIsCodeVerifying] = useState(false);
  const [phoneForVerification, setPhoneForVerification] = useState("");

  useEffect(() => {
    if (id && !hotelData.contactInfo) {
      fetchDraft(id);
    }
  }, [id, hotelData.contactInfo, fetchDraft]);

  useEffect(() => {
    if (hotelData.contactInfo) {
      setFormData(hotelData.contactInfo);
      if (hotelData.contactInfo.email) {
        setEmailForVerification(hotelData.contactInfo.email);
        setEmailVerificationStep("verified");
      }
      if (hotelData.contactInfo.phoneNumber) {
        setPhoneForVerification(hotelData.contactInfo.phoneNumber);
        setPhoneVerificationStep("verified");
      }
    }
  }, [hotelData.contactInfo]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSendCode = async () => {
    try {
      setIsEmailCodeSending(true);
      await generateEmailCode(formData.email);
      setEmailForVerification(formData.email);
      setEmailVerificationStep("code");
    } catch (err) {
      console.error("Failed to send code:", err);
    } finally {
      setIsEmailCodeSending(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setisEmailVerifying(true);
      const response = await VerifyEmailCode(emailForVerification, verificationCode);
      if (response.data?.verified || response.data?.success) {
        setEmailVerificationStep("verified");
        setFormData((prev) => ({
          ...prev,
          email: emailForVerification,
        }));
      } else {
        console.error("Email verification failed");
      }
    } catch (err) {
      console.error("Failed to verify code:", err);
    } finally {
      setisEmailVerifying(false);
    }
  };

  const handleSendSms = async () => {
    try {
      setIsCodeSending(true);
      await generateSmsCode("+852" + formData.phoneNumber);
      setPhoneForVerification(formData.phoneNumber);
      setPhoneVerificationStep("code");
    } catch (err) {
      console.error("Failed to send SMS code:", err);
    } finally {
      setIsCodeSending(false);
    }
  };

  const handleVerifySms = async () => {
    try {
      setIsCodeVerifying(true);
      const response = await VerifySmsCode(`+852${phoneForVerification}`, phoneSmsCode);
      if (response.data?.verified || response.data?.success) {
        setPhoneVerificationStep("verified");
        setFormData((prev) => ({
          ...prev,
          phoneNumber: phoneForVerification,
        }));
      } else {
        console.error("Phone verification failed");
      }
    } catch (err) {
      console.error("Failed to verify SMS code:", err);
    } finally {
      setIsCodeVerifying(false);
    }
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      setSaveError("");
      await saveStep("contactInfo", formData);
      navigate(edit ? `/hotel/${id}/information` : `/hotel/add/info5/${id}`);
    } catch (err) {
      const errorMessage = err.message || "Failed to save contact information";
      console.error("Failed to save step:", err);
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      await saveStep("contactInfo", formData);
      navigate(`/hotel/onboarding`);
    } catch (err) {
      const errorMessage = err.message || "Failed to save contact information";
      console.error("Failed to save step:", err);
      setSaveError(errorMessage);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? "#31BACD" : "transparent",
                    border: index === 0 ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index === 0 ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === 0 ? "600" : "400",
                    color: index === 0 ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: isMobile ? "20px" : "24px",
                color: "#000",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {t('HotelInfoStep4Title')}
              <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "14px",
                color: "#666",
              }}
            >
              {t('HotelInfoStep4Desc')}
            </Typography>
          </Box>

          {/* Form fields */}
          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 4, width: "100%" }}>
              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('PhoneNumberLabel')}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                {phoneVerificationStep !== "verified" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.phoneNumber}
                      placeholder="1234 5678"
                      onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                      InputProps={{
                        sx: {
                          bgcolor: "white",
                          borderRadius: "10px",
                          height: isMobile ? "45px" : "56px",
                          fontSize: isMobile ? "14px" : "16px",
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      disabled={formData.phoneNumber && !formData.phoneNumber.trim() || isCodeSending}
                      onClick={handleSendSms}
                      sx={{
                        backgroundColor: "#31BACD",
                        color: "#fff",
                        borderRadius: "10px",
                        textTransform: "none",
                        width: "120px",
                        fontSize: isMobile ? "12px" : "14px",
                        padding: "10px 20px",
                        whiteSpace: "nowrap",
                        "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                        "&:disabled": { opacity: 0.5, color: "#fff" },
                      }}
                    >
                      {isCodeSending ? <CircularProgress size={20} color="inherit" /> : t('SendCode')}
                    </Button>
                  </Box>
                )}
                {phoneVerificationStep === "verified" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.phoneNumber}
                      disabled
                      InputProps={{
                        sx: {
                          bgcolor: "#f5f5f5",
                          borderRadius: "10px",
                          height: isMobile ? "45px" : "56px",
                          fontSize: isMobile ? "14px" : "16px",
                        },
                      }}
                    />
                    <Box
                      component="img"
                      src="/icons/tick.svg"
                      alt="Verified"
                      sx={{ width: "110px", height: "24px", flexShrink: 0 }}
                    />
                  </Box>
                )}
              </Box>
              {phoneVerificationStep === "code" && (
                <Box>
                  <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                    {t('VerificationCodeLabel')}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  {phoneVerificationStep === "code" && (
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={"123456" || "Type in the verification code"}
                        value={phoneSmsCode}
                        onChange={(e) => setPhoneSmsCode(e.target.value)}
                        InputProps={{
                          sx: {
                            bgcolor: "white",
                            borderRadius: "10px",
                            height: isMobile ? "45px" : "56px",
                            fontSize: isMobile ? "14px" : "16px",
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        disabled={!phoneSmsCode.trim() || isCodeVerifying}
                        onClick={handleVerifySms}
                        sx={{
                          backgroundColor: "#31BACD",
                          color: "#fff",
                          width: "120px",
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: isMobile ? "12px" : "14px",
                          padding: "10px 20px",
                          whiteSpace: "nowrap",
                          "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                          "&:disabled": { opacity: 0.5, color: "#fff" },
                        }}
                      >
                        {isCodeVerifying ? <CircularProgress size={20} color="inherit" sx={{ color: "#31BACD" }} /> : t('Verify')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('SecondPhoneNumberLabel')}{" "}
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.secondPhoneNumber}
                    placeholder="1234 5678"
                    onChange={(e) => handleInputChange("secondPhoneNumber", e.target.value)}
                    InputProps={{
                      sx: {
                        bgcolor: "white",
                        borderRadius: "10px",
                        height: isMobile ? "45px" : "56px",
                        fontSize: isMobile ? "14px" : "16px",
                      },
                    }}
                  />
                  <Box sx={{ width: "130px", height: "auto" }}></Box>
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('HotelEmailLabel')}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                {emailVerificationStep !== "verified" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.email}
                      placeholder="example@gmail.com"
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      InputProps={{
                        sx: {
                          bgcolor: "white",
                          borderRadius: "10px",
                          height: isMobile ? "45px" : "56px",
                          fontSize: isMobile ? "14px" : "16px",
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      disabled={formData.email && !formData.email.trim() || isEmailCodeSending}
                      onClick={handleSendCode}
                      sx={{
                        backgroundColor: "#31BACD",
                        color: "#fff",
                        borderRadius: "10px",
                        textTransform: "none",
                        width: "120px",
                        fontSize: isMobile ? "12px" : "14px",
                        padding: "10px 20px",
                        whiteSpace: "nowrap",
                        "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                        "&:disabled": { opacity: 0.5, color: "#fff" },
                      }}
                    >
                      {isEmailCodeSending ? <CircularProgress size={20} color="inherit" sx={{ color: "#31BACD" }} /> : t('SendCode')}
                    </Button>
                  </Box>
                )}
                {emailVerificationStep === "verified" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.email}
                      disabled
                      InputProps={{
                        sx: {
                          bgcolor: "#f5f5f5",
                          borderRadius: "10px",
                          height: isMobile ? "45px" : "56px",
                          fontSize: isMobile ? "14px" : "16px",
                        },
                      }}
                    />
                    <Box
                      component="img"
                      src="/icons/tick.svg"
                      alt="Verified"
                      sx={{ width: "110px", height: "24px", flexShrink: 0 }}
                    />
                  </Box>
                )}
              </Box>

              {emailVerificationStep === "code" && (
                <Box>
                  <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                    {t('VerificationCodeLabel')}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  {emailVerificationStep === "code" && (
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={"123456" || "Type in the verification code"}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        InputProps={{
                          sx: {
                            bgcolor: "white",
                            borderRadius: "10px",
                            height: isMobile ? "45px" : "56px",
                            fontSize: isMobile ? "14px" : "16px",
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        disabled={!verificationCode.trim() || isEmailVerifying}
                        onClick={handleVerifyCode}
                        sx={{
                          backgroundColor: "#31BACD",
                          color: "#fff",
                          borderRadius: "10px",
                          textTransform: "none",
                          width: "120px",
                          fontSize: isMobile ? "12px" : "14px",
                          padding: "10px 20px",
                          whiteSpace: "nowrap",
                          "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                          "&:disabled": { opacity: 0.5, color: "#fff" },
                        }}
                      >
                        {isEmailVerifying ? <CircularProgress size={20} color="inherit" sx={{ color: "#31BACD" }} /> : t('Verify')}
                      </Button>
                    </Box>
                  )}

                </Box>
              )}

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('WhatsappLabel')}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.whatsapp}
                    placeholder="1234 5678"
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                    InputProps={{
                      sx: {
                        bgcolor: "white",
                        borderRadius: "10px",
                        height: isMobile ? "45px" : "56px",
                        fontSize: isMobile ? "14px" : "16px",
                      },
                    }}
                  />
                  <Box sx={{ width: "130px", height: "auto" }}></Box>
                </Box>
              </Box>
            </Box>
          )}
          {saveError && (
            <Box sx={{ width: "100%", mt: 2, p: 2, bgcolor: "#ffebee", borderRadius: "10px" }}>
              <Typography sx={{ color: "#d32f2f", fontSize: "14px" }}>
                {saveError}
              </Typography>
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={phoneVerificationStep !== "verified" || !formData.whatsapp.trim() || emailVerificationStep !== "verified" || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={phoneVerificationStep !== "verified" || !formData.whatsapp.trim() || emailVerificationStep !== "verified" || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_Information_4;

